package org.multiplatform.kotfolio.pages.koin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("koin")
@Composable
fun KoinPage() {
    BlogLayout(
        PageProperties(
            "Koin",
            "Koin is a lightweight pure kotlin framework designed to be straight forward in terms of usage and execution. Koin supports both Dependency Injection (DI) and the Service Locator (SL) pattern, offering flexibility to developers. Koin compatible with android, iOS, backend and web, it prioritizes clean and readable code."
        ),
        BlogProperties("Koin", "Basic")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {

            SpanText(
                text = "Koin 4.2.2",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    koin-plugin = "1.2.1"
                    koinBom = "4.2.2"
                    
                    koin-bom = { module = "io.insert-koin:koin-bom", version.ref = "koinBom" }
                    koin-core = { module = "io.insert-koin:koin-core" }
                    koin-compose = { module = "io.insert-koin:koin-compose" }
                    koin-compose-viewmodel = { module = "io.insert-koin:koin-compose-viewmodel" }
                    koin-compose-viewmodel-navigation = { module = "io.insert-koin:koin-compose-viewmodel-navigation" }
                    koin-android = { module = "io.insert-koin:koin-android" }
                    koin-ktor = { module = "io.insert-koin:koin-ktor", version.ref = "koinBom" }
                    koin-logger-slf4j = { module = "io.insert-koin:koin-logger-slf4j", version.ref = "koinBom" }
                    koin-test = { module = "io.insert-koin:koin-test", version.ref = "koinBom" }
                    """.trimIndent()
            )

            SpanText(
                text = "Constructor DSL auto-resolution",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Constructor DSL auto-resolution is a dependency injection feature that automatically inspects a class constructor and resolves its parameters from a container without manual lookup calls.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is prominently used in frameworks like Koin via references like single(::class) instead of an explicit lambda block.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/ioannisa/AppFunctions-Demo-App.git
            KotlinCode(
                code = """
                    //Constructor DSL auto-resolution
                    factory<JokesRepository> { new(::JokesRepositoryImpl) }

                    viewModel { new(::JokesListViewModel) }
                    viewModel { parameters -> JokesDetailsViewModel(joke = parameters.get(), repository = get()) }
                    
                    //lamda reference
                    single { (::GetLanguageUseCase) (BlogsRepository()::getLanguage) } //Manually every time the singleton is created.
                    
                    single { (::GetLanguageUseCase) (get<BlogsRepository>()::getLanguage) }
                    single { (::GetTopicForLanguageUseCase) (get<BlogsRepository>()::getTopic) }
                    single { (::GetTracksForTopicUseCase) (get<BlogsRepository>()::getTrack) }
                
                    //object
                    single { KtorClient.httpClient }
                    
                    single { CoroutineScope(SupervisorJob()) }
                    
                    //interface
                    single<LocalJokesDataSource> { new(::LocalJokesDataSourceImpl) }
                    
                    class BlogsRepository {
                        fun getTrack(topicId: String): Flow<ImmutableList<Blog>> = flow {
                            emit(tracks[topicId]?.toImmutableList() ?: persistentListOf())
                        }
                    }
                    
                    //class
                    single<JokesAppFunctions> { new(::JokesAppFunctions) }
                    single { new(::BlogsRepository) } withOptions {  createdAtStart() }
                   
                   object KtorClient {
                        val httpClient = HttpClient(OkHttp) { }
                   }
                   
                    class JokesAppFunctions(
                        val repository: JokesRepository
                    ) { }
                    
                    @Stable
                    class JokesDetailsViewModel(
                        joke: Joke,
                        private val repository: JokesRepository
                    ) : ViewModel() { }
                    """.trimIndent()
            )

            SpanText(
                text = "rememberKoinModules",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/youndie/mani.git
            KotlinCode(
                code = """
                    @Composable
                    fun LoginComponent(appBarState: MainAppBarState, onSignupClicked: () -> Unit, onSuccess: () -> Unit) {
                        rememberKoinModules {
                            listOf(
                                module {
                                    singleOf(::LoginUseCase).bind<AuthUseCase>()
                                    viewModelOf(::AuthViewModel)
                                },
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/IgnacioCarrionN/KMPKoin
            //https://github.com/VolodymyrPal/TrainingSession.git

            SpanText(
                text = "Koin 4.2.1",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://blog.insert-koin.io/unlocking-koin-compile-safety-6278840ab171
            KotlinCode(
                code = """
                    kotlin = ""
                    koin = "4.2.1"
                    koin-plugin = "1.0.0"
                    
                    [libraries]
                    koin-core = { module = "io.insert-koin:koin-core", version.ref = "koin" }
                    koin-android = { module = "io.insert-koin:koin-android", version.ref = "koin" }
                    koin-compose = { module = "io.insert-koin:koin-compose", version.ref = "koin" }
                    koin-compose-viewmodel = { module = "io.insert-koin:koin-compose-viewmodel", version.ref = "koin" }
                    koin-annotations = { module = "io.insert-koin:koin-annotations", version.ref = "koin" }
                    
                    koin-compiler = { id = "io.insert-koin.compiler.plugin", version.ref = "koin-plugin" }
                    
                    plugins {
                        alias(libs.plugins.koin.compiler)
                    }
                    
                    koinCompiler {
                        userLogs = true           // Log component detection
                        debugLogs = true          // Log internal processing (verbose)
                        dslSafetyChecks = true    // Validates create() is the only instruction in lambda (default: true)
                        unsafeDslChecks = true    // Validates create() is the only instruction in lambda (default: true)
                        skipDefaultValues = true  // Skip injection for parameters with default values (default: true)
                        compileSafety = true      // Activate compile safety verification for the current module
                    }
                    
                    //HttpClient
                    fun createHttpClientWithConfig(): HttpClient { }
                    //single { createHttpClientWithConfig() } 
                    
                    //NEW COMPILER PLUGIN: single‹Class>(), factory<Class>(), scoped<Class>(), viewModel<VM>()
                    //CLASSIC AUTOWIRE: singleOf(::Class), factoryOf(::Class), scopedOf(::Class), viewModelOf(::VM)
                    
                    single { create(::createHttpClientWithConfig) } // Safe function builder call
                    
                    single<PaymentRepositoryImpl>() bind PaymentRepository:: class

                    single<ProcessPaymentUseCase>()
                    single<AppStateManager>()
                    //factory<ProcessPaymentUseCase>()

                    //Parameter
                    viewModel { parameters ->
                        //paymentId = parameters.get()
                        ConfirmPaymentViewModel(get())
                    }
                    viewModel<StatusPaymentViewModel>()
                    
                    //val random = koin.get<Int>(qualifier = named("random"), parameters = { parametersOf(100) })
                    
                    //withOptions
                    single<MuseumRepository>() withOptions { createdAtStart() }
                    """.trimIndent()
            )

            SpanText(
                text = "Full Graph (complete guarantee)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "At startKoin<T>(), all modules from all sources are assembled, and the complete graph is validated. Everything that the per-module phase couldn’t check (cross-module dependencies, external definitions) is checked here. Every definition across every module, validated together, on every build.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "Koin 4.1.0 New Features",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A more powerful configuration system",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A redesigned resolver engine",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Easier and safer scoping with archetypes",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stronger Compose and Ktor integrations",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expanded test and multiplatform support",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                        //Start Configuration Extension
                        
                        fun initKoin(config : KoinAppDeclaration? = null){
                            startKoin {
                                includes(config)
                                modules(appModule)
                            }
                        }
                        
                        class MainApplication : Application() {
                            override fun onCreate() {
                                super.onCreate()

                                initKoin {
                                    androidContext(this@MainApplication)
                                    androidLogger()
                                }
                            }
                        }
                        
                        public class MainJavaApplication extends Application {
                            @Override
                            public void onCreate() {
                                super.onCreate();

                                KoinApplication koin =
                                        KoinAndroidApplication.create(this)
                                                .printLogger(Level.INFO)
                                                .modules(Collections.<Module>emptyList());
                                startKoin(koin);
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //1
                    @Composable
                    fun App() {
                        KoinMultiplatformApplication(config = koinConfig) {
                            // ...
                        }
                    }

                    //2
                    KoinMultiplatformApplication(
                        config = koinConfiguration { modules(applicationModules()) }
                    ) {
                            AppThemeContainer {
                            RootScreen()
                        }
                    }
                    fun applicationModules() = listOf(homeModule) + dataModule()


                    //3 Dynamic Parameter Injection
                    fun MyComposable(
                        myFactory: MyFactory = koinInject(parameters = parametersOf("first_string"))
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Scope - (Archetype scope)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scoped dependencies to keep data alive just long enough for a specific user flow — like a checkout session — without bloating global ViewModels or risking stale state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                        //Feature Options Runtime
                        
                        startKoin {
                            options(
                                viewModelScopeFactory()
                            )

                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Scope Archetypes
                        
                        module {
                          // archetype scope - usable by any Activity
                          activityScope {
                              scopedOf(::Session)
                          }
                          
                          // archetype scope - usable by Fragment
                          fragmentScope {
                            // ...
                          }
                        }

                        class MVVMActivity : ScopeActivity(contentLayoutId = R.layout.mvvm_activity) { } 

                        class ScopedFragment : Fragment(R.layout.mvvm_fragment), AndroidScopeComponent {
                            override val scope: Scope by fragmentScope()
                        }
                        
                        //2
                        val scopedModule = module {
                            // Define a scope tied to a specific lifecycle
                            scope<MainActivity> {
                                // This instance lives only while MainActivity exists
                                scoped { MainActivityPresenter(get()) }

                                // Factory still creates new instances within the scope
                                factory { SomeDialogHelper(get()) }
                            }
                        }
                        
                        class MainActivity : AppCompatActivity(), KoinScopeComponent {
                            // Create a scope tied to this activity
                            override val scope: Scope by activityScope()

                            // Inject scoped dependencies
                            private val presenter: MainActivityPresenter by inject()

                            override fun onDestroy() {
                                super.onDestroy()
                                // Scope is automatically closed when activity is destroyed
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                        //ScopedKoinContainer Pattern
                        //We created a reusable wrapper that manages the creation and destruction of a Koin scope based on the Composable’s presence in the composition.
                        
                        @Composable
                        fun PaymentMethodsScreen(
                            onNavigateBack: () -> Unit,
                            onCardSelected: (Card) -> Unit,
                        ) {
                            // Automatically creates and manages Koin scope
                            ScopedKoinContainer<PaymentMethodScope>(
                                modules = listOf(
                                    paymentMethodDomainModule,
                                    paymentMethodDataModule,
                                )
                            ) {
                                // Inside this block, scoped dependencies are available
                                PaymentMethodsContent(
                                    onNavigateBack = onNavigateBack,
                                    onCardSelected = onCardSelected,
                                )
                            }
                        }
                        
                        //ScopedKoinContainer Works
                        @Composable
                        inline fun <reified T : Any> ScopedKoinContainer(
                            modules: List<Module>,
                            crossinline content: @Composable () -> Unit,
                        ) {
                            val uniqueId = remember { "${T::class.qualifiedName}_${UUID.randomUUID()}" }
                            
                            DisposableEffect(uniqueId) {
                                // Load modules into Koin dynamically
                                getKoin().loadModules(modules)

                                // Create the unique scope
                                val scope = getKoin().createScope<T>(uniqueId)
                                
                               // Cleanup when leaving composition
                                onDispose {
                                    scope.close()
                                    getKoin().unloadModules(modules)
                                }
                            }
                            
                            val scope = remember(uniqueId) { getKoin().getScope(uniqueId) }

                            // Provide scope to children via CompositionLocal
                            CompositionLocalProvider(LocalKoinScope provides scope) {
                                content()
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //ScopeViewModel
                        //From the previous versions, Koin offered the ScopeViewModel abstract class to help you handle Scope creation for a ViewModel. This support class will not be needed anymore.
                      
                        @OptIn(KoinExperimentalAPI::class)
                        class MyScopeViewModel2 : ScopeViewModel() { }
                        
                        //viewModelScope
                        //The viewModelScope() Scope API now leverages Android’s AutoCloseable API to let us automatically handle lifecycle setup and cleanup—no more manual scope closing callback is necessary.
                        //Way to bind a class (like a ViewModel) to a Koin scope, so dependencies live only as long as that scope does.
                        //Scoped objects (user sessions, screens, flows) 
                        //More flexible, supports multiple lifecycles
                        //Scoped Koin container
                       
                        class MyScopeViewModel : ViewModel(), KoinScopeComponent {
                            // create and destroy ViewModel scope
                            override val scope: Scope = viewModelScope()
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         //Access Scope ViewModel
                         
                         // Create scope for this ViewModel instance
                         class HomeViewModel : ViewModel(), KoinScopeComponent {
                             override val scope: Scope by lazy { createScope(this) }
                             //override val scope: Scope = createScope(this)
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //ViewModel Constructor Injection with Scope
                        
                        startKoin {
                            options(
                                viewModelScopeFactory()
                            )
                        }
                        
                        class MyViewModel(val session: Session) : ViewModel()
                        module {
                          viewModelOf(::MyViewModel)
                          
                          viewModelScope {
                              scopedOf(::Session)
                          }
                          
                        }
                        
                        class MVPActivity : AppCompatActivity() {
                            // Inject ViewModel with its scoped constructor arguments
                            val myVM: MyViewModel by ViewModel()
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Modules and Lazy Modules as a Configuration
                        
                        val lazyModule = lazyModule {
                            single {  }
                        }
                        
                        startKoin {
                          moduleConfiguration {
                            modules(appModule)
                            lazyModules(lazyModule)
                          }
                        }

                        //or
                        startKoin {
                          moduleConfiguration(config)
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Instead of singletons, we define a feature scope that bounds the lifespan of the objects to the feature’s lifecycle.
                        
                        // Define the scope marker
                        class PaymentMethodScope

                        // Define the module with scoped dependencies
                        val paymentMethodDomainModule = module {
                            scope<PaymentMethodScope> {
                                scopedOf(::DeleteCardUseCase)
                                scopedOf(::GetAllCardsUseCase)
                            }
                        }

                        val paymentMethodDataModule = module {
                            scope<PaymentMethodScope> {
                                scopedOf(::PaymentMethodRepository)
                                scopedOf(::CardApiService)
                            }
                        }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each screen shares the same scoped dependencies (CartManager, PaymentManager, ReceiptManager) via a CheckoutScope that stays alive for the entire checkout flow — and closes automatically when the flow ends.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cart Screen → Add and remove products",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Payment Screen → Complete the transaction",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Receipt Screen → Show purchase summary",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://github.com/donald-okara/ScopeExampl.git
            KotlinCode(
                code = $$"""
                        class CheckoutScope: KoinScopeComponent {
                            override val scope: Scope by lazy { createScope(this) }
                            
                            // Inject scoped dependencies
                            val viewModel: ProfileViewModel by scope.inject()

                            fun close() = scope.close()
                        }

                        class CartManager {
                            val items = mutableStateListOf<ShoppingItem>()  // ✅ reactive list
                            fun addToCart(
                                item: ShoppingItem,
                            ){
                                items.add(item)
                            }

                            fun removeFromCart(
                                item: ShoppingItem,
                            ){
                                items.remove(item)
                            }
                        }

                        class PaymentManager(private val cartManager: CartManager){
                            val items get() = cartManager.items
                            fun checkout(){
                                println("Checkout items: ${cartManager.items}")
                            }
                        }

                        class ReceiptManager(private val paymentManager: PaymentManager){
                            val items get() = paymentManager.items
                        }
                        
                        //ScopedModule.kt
                        val scopeModule = module {
                            scope<CheckoutScope> {
                                scopedOf(::CartManager)
                                scopedOf(::PaymentManager)
                                scopedOf(::ReceiptManager)
                            }
                        }
                        
                        fun initKoin(config: KoinAppDeclaration? = null) {
                            startKoin {
                                config?.invoke(this)
                                modules(
                                    scopeModule
                                )
                            }
                        }
                        
                        @Composable
                        fun CartScreenContent(
                            modifier: Modifier = Modifier,
                            cartManager: CartManager,
                            onProceedToCheckout: () -> Unit
                        ) {
                             val items = cartManager.items  // no need for remember or mutableStateOf
                        }
                        
                        class PaymentScreen(private val scopeID: String): Screen {
                            @OptIn(ExperimentalMaterial3Api::class)
                            @Composable
                            override fun Content() {
                                val navigator = LocalNavigator.current
                                val koin = getKoin()
                                val scope = remember { koin.getScope(scopeID) }

                                val paymentManager: PaymentManager = scope.get()
                            }
                        }
                        
                        class ReceiptScreen(private val scopeID: String): Screen {
                            @OptIn(ExperimentalMaterial3Api::class)
                            @Composable
                            override fun Content() {
                                val navigator = LocalNavigator.current
                                val koin = getKoin()
                                val scope = remember { koin.getScope(scopeID) }
                                val receiptManager: ReceiptManager = scope.get()

                            }
                        }
                        
                        object CheckoutFlow : Screen {
                            @OptIn(ExperimentalComposeUiApi::class)
                            @Composable
                            override fun Content() {
                                val scope = remember { CheckoutScope() }

                                Navigator(
                                    screen = CartScreen(scope.scope.id),
                                ){ navigator ->
                                    AnimatedContent(
                                        targetState = navigator.lastItem,
                                        transitionSpec = {
                                            if (navigator.lastEvent == StackEvent.Pop) {
                                                (scaleIn(initialScale = 1.2f) + fadeIn()) togetherWith
                                                        (scaleOut(targetScale = 0.8f) + fadeOut())
                                            } else {
                                                (scaleIn(initialScale = 0.8f) + fadeIn()) togetherWith
                                                        (scaleOut(targetScale = 1.2f) + fadeOut())
                                            }
                                        },
                                    ) { screen ->
                                        screen.Content()
                                    }
                                }

                                BackHandler{
                                    scope.close()
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                        //ViewModel using Scope
                        
                        val checkoutModule = module {
                            scope(named("CHECKOUT_SCOPE")) {
                                scoped { CartManager() }
                                scoped { PaymentManager(get()) }
                                scoped { ReceiptManager(get()) }
                            }

                            viewModel { (scopeId: String) ->
                                CheckoutViewModel(scopeId)
                            }
                        }

                        class CartManager {
                            private val items = mutableListOf<String>()

                            fun addItem(item: String) {
                                items.add(item)
                            }

                            fun getItems(): List<String> = items

                            fun clear() {
                                items.clear()
                            }
                        }

                        class PaymentManager(
                            private val cartManager: CartManager
                        ) {
                            fun pay(): Boolean {
                                return cartManager.getItems().isNotEmpty()
                            }
                        }

                        class ReceiptManager(
                            private val cartManager: CartManager
                        ) {
                            fun generateReceipt(): String {
                                return "Receipt: ${cartManager.getItems().joinToString()}"
                            }
                        }

                        class CheckoutViewModel(scopeId: String) : ViewModel(), KoinScopeComponent {
                            override val scope: Scope = getKoin().createScope(scopeId, named("CHECKOUT_SCOPE"))

                            private val cartManager: CartManager by scope.inject()
                            private val paymentManager: PaymentManager by scope.inject()
                            private val receiptManager: ReceiptManager by scope.inject()

                            // Cart
                            fun addItem(item: String) {
                                cartManager.addItem(item)
                            }

                            fun getItems(): List<String> {
                                return cartManager.getItems()
                            }

                            // Payment
                            fun pay(): Boolean {
                                return paymentManager.pay()
                            }

                            // Receipt
                            fun getReceipt(): String {
                                return receiptManager.generateReceipt()
                            }

                            override fun onCleared() {
                                super.onCleared()
                                scope.close()   // Important!
                            }
                        }

                        @OptIn(ExperimentalUuidApi::class)
                        @Composable
                        fun CheckoutScreen () {
                            val scopeId = remember { "checkout_${Uuid.random()}" }
                            //val scopeId = remember { "checkout" } //static scope id

                            val viewModel: CheckoutViewModel = koinViewModel(
                                key = scopeId,
                                parameters = { parametersOf(scopeId) }
                            )

                            Column {
                                Button(onClick = {
                                    viewModel.addItem("Item")
                                }) {
                                    Text("Add Item")
                                }

                                Button(onClick = {
                                    val result = viewModel.pay()
                                    println("Paid: $result")
                                }) {
                                    Text("Pay")
                                }

                                Button(onClick = {
                                    println(viewModel.getReceipt())
                                }) {
                                    Text("Generate Receipt")
                                }
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "voyager using Koin Parameter",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/NatashaIlievska/Pokemon-CMP
            KotlinCode(
                code = """
                            //1
                            import org.koin.dsl.module

                            val viewModelModule = module {
                                factory {
                                    HomeViewModel(get())
                                }

                                factory { (id: Int) ->
                                    DetailsViewModel(id = id, getPokemonByIdUseCase = get())
                                }
                            }

                            //2
                            class DetailsViewModel(val id: Int) : ScreenModel {}

                            //3
                            import org.koin.core.parameter.parametersOf
                            import cafe.adriel.voyager.core.screen.Screen
                            import cafe.adriel.voyager.koin.koinScreenModel

                            class DetailsScreen(val id: Int) : Screen {
                                @Composable
                                override fun Content() {
                                    val viewModel = koinScreenModel<DetailsViewModel>(
                                        parameters = { parametersOf(id) }
                                    )
                                }
                            }
                    """.trimIndent()
            )

            SpanText(
                text = "Koin 4.0 New Features",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "Koin 4.0 introduces a major improvement of the ViewModel API, transitioning to a full multiplatform approach. The new koin-core-viewmodel package centralizes the ViewModel APIs, making them reusable across different platforms. Removed old deprecated methods like get, inject, and stateViewModel in favor of the newer Compose integrations. The new koin-core-viewmodel package centralizes ViewModel APIs, making them reusable across Android, Jetpack Compose, and other platforms.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            KotlinCode(
                code = """
                    //Koin 4.0 ViewModel

                    import org.koin.core.module.dsl.viewModelof

                    val viewModelModule: Module = module {
                        viewModelOf(::MyViewModel)
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "Koin 4.0.1 New Features",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //𝖪𝗈𝗂𝗇𝖲𝗍𝖺𝗋𝗍𝗎𝗉𝖤𝗑𝗍𝖾𝗇𝗌𝗂𝗈𝗇𝖨𝗇𝗂𝗍𝗂𝖺𝗅𝗂𝗓𝖾𝗋

                    val featureModule = lazyModule {
                        single { FeatureServices ()
                    }

                    class FeatureInitializer : KoinStartupExtensionInitializer<Unit>()
                    {
                        override fun create(context: Context) {
                            loadLazyModules (featureModule)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "module of list",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/tkuitocc/icampuspass-kmp
            KotlinCode(
                code = """
                     val commonModule = module {
                        
                     }

                     expect val platformModule: Module

                     fun initKoin(
                         extraModules: List<Module>
                     ) {
                         startKoin {
                             modules(
                                 commonModule,
                                 platformModule,
                                 *extraModules.toTypedArray(),
                             )
                         }
                     }

                     fun initKoin() = initKoin(extraModules = emptyList())
                     
                     //ios
                     init() {
                        KoinKt.doInitKoin()
                     }
                     
                     //android
                     class AndroidApp : Application() {
                         override fun onCreate() {
                             super.onCreate()

                             initKoin(
                                 androidContext = this@AndroidApp,
                                 extraModules = listOf(
                                     module {
                                         factory<MainViewModel> {
                                             MainViewModel(appRepository = get())
                                         }
                                     }
                                 )
                             )
                         }
                     }
                """.trimIndent()
            )

            SpanText(
                text = "KoinPlatform",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/mitulbharatchandra/WorldWideMoto.git
            KotlinCode(
                code = """
                     KoinPlatform.getKoin().get<FirebaseInitializer>().initialize()
                     
                     expect class FirebaseInitializer {
                         fun initialize()
                     }
                    """.trimIndent()
            )

            SpanText(
                text = "DSL DI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/steve1rm/BusbyMoviesV2
            //https://github.com/KevinBlock-GC/kmp-firebase-auth
            KotlinCode(
                code = """
                    val configurationModule = module {
                        singleOf(::FirebaseServiceImpl).bind<FirebaseService>()
                        viewModelOf(::WelcomeViewModel).bind<WelcomeViewModel>()
                        //viewModelOf(::WelcomeViewModel)
                        
                        single<Clock> { Clock.System }
                        singleOf(HttpClientFactory::create)
                        
                        factoryOf(::ConfigurationUseCaseImp).bind(ConfigurationUseCase::class)
                        factoryOf(::ConfigurationRemoteDataSourceImp).bind<ConfigurationRemoteDataSource>()
                        factoryOf(::ConfigurationRepositoryImp).bind(ConfigurationRepository::class)
                    }
                    
                    object HttpClientFactory {
                        fun create(): HttpClient = HttpClient(createHttpEngine()) {
                            engine { dispatcher = Dispatchers.IO }
                        }
                    }
                    
                    interface FirebaseService {
                        fun getCurrentApp(): FirebaseAppInfo
                        fun getCurrentUser(): AppUser?
                        suspend fun signIn(email: String, password: String): AppUser
                        suspend fun setDisplayName(displayName: String)
                        suspend fun createUser(email: String, password: String, displayName: String): AppUser
                        suspend fun signOut()
                    }
                    
                    class FirebaseServiceImpl : FirebaseService { }
                    
                    class WelcomeViewModel(
                        private val coroutineDispatcher: AppCoroutineDispatcher,
                        private val firebaseService: FirebaseService,
                    ) : ViewModel() { }


                    val coreDataModule = module {
                        single<HttpClient> {
                            HttpNetworkClientImp(get<HttpClientEngine>()).build()
                        }

                        single<String> {
                            "This is has been initialized - koin is working"
                        }

                        single { Json { ignoreUnknownKeys = true } }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Koin initializer Module",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/TaxiKab119/meals-cmp-demo
            KotlinCode(
                code = """
                    //Koin 4.0.0

                    //commonMain
                    fun initKoin(vararg modules: Module) = startKoin {
                        modules(modules.toList())
                    }

                    fun createHttpClient(engine: HttpClientEngine): HttpClient {
                        return HttpClient(engine) {
                            install(Logging) {
                                level = LogLevel.ALL // ensures the entire request is logged
                            }

                            install(ContentNegotiation) {
                                json(
                                    json = Json {
                                        ignoreUnknownKeys = true // this ensures that if the api call returns some json fields that we have not declared in our data class that it doesn't just fail
                                    }
                                )
                            }
                        }
                    }

                    val commonModule = module {

                    }

                    //iosMain
                    fun MainViewController() = ComposeUIViewController(
                        configure = { initKoin(iosNetworkModule, commonModule) }
                    ) {
                        App()
                    }

                    import app.sizzle.cmp.core.data.createHttpClient
                    import app.sizzle.cmp.meals.data.MealsDbClient
                    import io.ktor.client.engine.darwin.Darwin
                    import org.koin.dsl.module

                    val iosNetworkModule = module {
                        single { createHttpClient(Darwin.create()) }
                        single { MealsDbClient(get()) } // Inject iOS specific HttpClient into MealsDbClient
                    }

                    //androidMain
                    import app.sizzle.cmp.core.data.createHttpClient
                    import app.sizzle.cmp.meals.data.MealsDbClient
                    import io.ktor.client.engine.okhttp.OkHttp
                    import org.koin.dsl.module

                    val androidNetworkingModule = module {
                        single { createHttpClient(OkHttp.create()) }
                        single { MealsDbClient(get()) } // Inject Android Specific HttpClient into MealsDbClient
                    }

                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)

                            startKoin {
                                androidContext(this@MainActivity)
                                modules(androidNetworkingModule, commonModule)
                            }

                            setContent {
                                App()
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/bimovall/chess-player-viewer
            KotlinCode(
                code = """
                    val appModule = listOf(provideHttpClient, provideDataSourceModule, viewModelModule, provideDomainModule)

                    fun initKoin(config: KoinConfiguration? = null, vararg additionalModule: Module) {
                        startKoin {
                            includes(config)
                            modules(appModule + additionalModule)
                        }
                    }
                    
                    expect class DatabaseDriverFactory {
                        fun createDriver(): SqlDriver
                    }
                    
                    //android
                    class ChessApplication: Application() {

                        override fun onCreate() {
                            super.onCreate()

                            initKoin(config = KoinConfiguration {
                                androidContext(this@ChessApplication)
                            }, androidModule())
                        }
                    }
                    
                    actual class DatabaseDriverFactory(private val context: Context) {
                        actual fun createDriver(): SqlDriver {
                            return AndroidSqliteDriver(ChessPlayerDB.Schema, context, "chess_player.db")
                        }
                    }
                    
                    fun androidModule() = module {
                        singleOf(::DatabaseDriverFactory)

                        single<SqlDriver> {
                            get <DatabaseDriverFactory>().createDriver()
                        }
                    }
                    
                    //ios
                    actual class DatabaseDriverFactory{
                        actual fun createDriver(): SqlDriver {
                            return NativeSqliteDriver(ChessPlayerDB.Schema, "chess_player.db")
                        }
                    }
                    
                    fun iosModule() = module {
                        singleOf(::DatabaseDriverFactory)

                        single<SqlDriver> {
                            get <DatabaseDriverFactory>().createDriver()
                        }
                    }
                    
                    fun MainViewController() = ComposeUIViewController(
                        configure = {
                            initKoin(additionalModule = arrayOf(iosModule()))
                        }
                    ) {
                        App()
                    }
                    """.trimIndent()
            )

            //Obtain the Koin Application Instance
            //Creating Methods to Retrieve Instances
            //Using these get methods

            SpanText(
                text = "Within module, various types of components",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "KoinComponents",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KoinComponent is an interface that allows your class to directly access dependencies from the Koin container without explicitly passing them around.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ol {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "by inject() - lazy evaluated instance from Koin container.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                //https://github.com/aslibyr/MoviesKMM
                KotlinCode(
                    code = """
                        private val domainModule = module {
                            factory { GetMoviesUseCase() }
                            single<MovieRepository> { MovieRepositoryImpl(get()) }
                        }

                        internal interface MovieRepository {
                            suspend fun getMovies(page: Int): List<MovieModel>
                            suspend fun getMovieDetails(movieId: Int): MovieModel
                        }

                        class GetMoviesUseCase : KoinComponent {
                            private val movieRepository: MovieRepository by inject()

                            @Throws(Exception::class)
                            suspend operator fun invoke(page: Int = 1): List<MovieModel> {
                                return movieRepository.getMovies(page)
                            }
                        }
                        """.trimIndent()
                )

                //https://github.com/mhmmdvg/playit-kmp
                KotlinCode(
                    code = """
                        class KoinHelper : KoinComponent {
                            private val playlistsRepository: PlaylistsRepository by inject()
                            fun providePlaylistsRepository(): PlaylistsRepository = playlistsRepository

                                companion object {
                                    val shared = KoinHelper()
                                }
                        }
                        """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "get() - eager fetch instance from Koin container.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "getProperty()/setProperty() - get/set property.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //di
                    private val apiModule = module {
                        factory { BreedsApi() }
                    }
                    
                    private val repositoryModule = module {
                        single { BreedsRepository(get()) }
                    }
                    
                    private val usecaseModule = module {
                        factory { GetBreedsUseCase() }
                    }

                    internal class BreedsApi: KtorApi() {
                        suspend fun getBreeds(): BreedsResponse = client.get {
                            apiUrl("breeds/list")
                        }.body()
                    }
                    
                    internal abstract class KtorApi {
                        private val jsonConfiguration = Json {
                            prettyPrint = true
                            ignoreUnknownKeys = true
                        }

                        val client = HttpClient {
                            install(ContentNegotiation) {
                                json()
                            }
                            
                            install(Logging) {
                                logger = Logger.SIMPLE
                                level = LogLevel.ALL
                            }
                        }

                        /**
                         * Use this method for configuring the request url
                         */
                        fun HttpRequestBuilder.apiUrl(path: String) {
                            url {
                                takeFrom("https://dog.ceo")
                                path("api", path)
                            }
                        }
                    }
                    
                    internal class BreedsRemoteSource (
                        private val api: BreedsApi,
                        private val dispatcherProvider: DispatcherProvider,
                    ) {

                        suspend fun getBreeds() = withContext(dispatcherProvider.io) {
                            api.getBreeds().breeds
                        }
                    }
                    
                    class BreedsRepository internal constructor(
                        private val remoteSource: BreedsRemoteSource
                    ) {
                        suspend fun get() = fetch()

                        suspend fun fetch() = supervisorScope {
                            remoteSource.getBreeds().map {
                                async { Breed(name = it, imageUrl = remoteSource.getBreedImage(it))}
                            }.awaitAll()
                        }
                    }
                    
                    class FetchBreedsUseCase: KoinComponent {
                        private val breedsRepository: BreedsRepository by inject()
                        
                        suspend fun invoke(): List<Breed> = breedsRepository.fetch()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "module",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //true - Eager initialization — single objects are created as soon as Koin starts.
                    //false (default) - Lazy initialization — single objects are created only when first needed.
                    
                    val newsModule = module(createdAtStart = true) { 
                    	includes(newsCommonModule)
                    }
                    """.trimIndent()
            )

            //https://github.com/arrazyfathan/tudu-mobile
            KotlinCode(
                code = """
                    expect val platformModule: Module
                    expect val preferencesModule: Module

                    val dispatcherModule =
                        module {
                            single<CoroutineDispatcher>(qualifier = named("io")) { Dispatchers.IO }
                        }

                    val sharedModule =
                        module {
                            single {
                                HttpClientFactory(get()).build(get())
                            }

                            single<PreferencesManager> {
                                PreferencesManagerImpl(get(), get(named("io")))
                            }
                            
                            singleOf(::AuthenticationRepositoryImpl).bind<AuthenticationRepository>()

                            viewModelOf(::LoginViewModel)
                            factoryOf(::LoginUseCase)
                        }

                    fun initKoin(config: KoinAppDeclaration? = null) {
                        startKoin {
                            config?.invoke(this)
                            modules(sharedModule, platformModule, preferencesModule, dispatcherModule)
                        }
                    }
                    
                    //androidMain
                    class TuduApp : Application() {
                        override fun onCreate() {
                            super.onCreate()
                            Napier.base(DebugAntilog())
                            initKoin {
                                androidContext(this@TuduApp)
                            }
                        }
                    }
                    
                    //createDataStore.android.kt
                    import android.content.Context
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import androidx.compose.ui.platform.LocalContext
                    import androidx.datastore.core.DataStore
                    import androidx.datastore.preferences.core.Preferences

                    fun createDataStore(context: Context): DataStore<Preferences> {
                        return createDataStore(
                            producePath = { context.filesDir.resolve(DATA_STORE_FILE_NAME).absolutePath },
                        )
                    }

                    @Composable
                    actual fun rememberDataStore(): PrefsDataStore {
                        val context = LocalContext.current
                        return remember { createDataStore(context) }
                    }

                    //Module.android.kt
                    actual val platformModule: Module
                        get() =
                            module {
                                single<HttpClientEngine> { OkHttp.create() }
                            }
                    actual val preferencesModule: Module
                        get() =
                            module {
                                single { createDataStore(androidContext()) }
                            }
                            
                    //iosMain
                    //createDataStore.ios.kt
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import androidx.datastore.core.DataStore
                    import androidx.datastore.preferences.core.Preferences
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.Foundation.NSDocumentDirectory
                    import platform.Foundation.NSFileManager
                    import platform.Foundation.NSURL
                    import platform.Foundation.NSUserDomainMask

                    @OptIn(ExperimentalForeignApi::class)
                    fun createDataStore(): DataStore<Preferences> =
                        createDataStore(
                            producePath = {
                                val documentDirectory: NSURL? =
                                    NSFileManager.defaultManager.URLForDirectory(
                                        directory = NSDocumentDirectory,
                                        inDomain = NSUserDomainMask,
                                        appropriateForURL = null,
                                        create = false,
                                        error = null,
                                    )
                                requireNotNull(documentDirectory).path + "/${'$'}DATA_STORE_FILE_NAME"
                            },
                        )

                    //Module.ios.kt    
                    @Composable
                    actual fun rememberDataStore(): PrefsDataStore {
                        return remember { createDataStore() }
                    }
                    
                    actual val platformModule: Module
                        get() =
                            module {
                                single<HttpClientEngine> { Darwin.create() }
                            }

                    actual val preferencesModule: Module
                        get() =
                            module {
                                single { createDataStore() }
                            }
            
          
                """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This keyword is used to define a Koin module. You can use it to group together a set of related dependencies and provide additional configuration options, such as scoping and naming.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "createdAtStart",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By default, Koin is LAZY — instances created on first access. Use createdAtStart for critical instances that must exist immediately",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "single",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Declares a singleton component that is created only once and shared across the application. A singleton is an object that is created once and reused throughout the lifetime of the application. You can use the single keyword to define a singleton dependency within a module. Ideal for shared resources like repositories, database instances, or network clients.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "factory",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Declares a new instance every time the dependency is injected. A factory is an object that creates and returns a new instance of a class each time it is called. You can use the factory keyword to define a factory dependency within a module. Suitable for components that should not be shared or Holds no state, such as utility objects or lightweight objects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/InonELGABSI/TopOut
            KotlinCode(
                code = """
                    factory { (scope: CoroutineScope) -> }
                    
                    single<CoroutineScope> { CoroutineScope(SupervisorJob() + Dispatchers.Default) }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class HomeViewModel(private val repository: NetworkRepository) : ViewModel() { }

                    class MovieDetailViewModel(
                        private val movieId: Long,
                        private val repository: NetworkRepository,
                    ) : ViewModel() { }

                    val provideViewModelModule = module {
                        factory { HomeViewModel(get()) }

                        factory { (movieId: Long) -> MovieDetailViewModel(movieId, get()) }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "scoped",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Declares a scoped component, where the lifecycle is tied to a specific scope. A scoped dependency is a dependency that is tied to a specific scope, such as a particular activity or fragment. You can use the scoped keyword to define a scoped dependency within a module. Useful for scoping dependencies to features, workflows, or ViewModel lifecycles.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "bind",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This keyword is used to bind a dependency to an interface or a parent class. You can use the bind keyword to provide a more abstract interface to your dependencies, making it easier to swap out implementations later.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //bind to interface

                    //@Single
                    //@Bind([AuthService::class])

                    @Single(binds = [AuthService::class])
                    class FirebaseAuthService : AuthService
                    """.trimIndent()
            )

            SpanText(
                text = "get",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This keyword is used to retrieve a dependency from Koin. You can use it to get a dependency by its type or name, either directly or through a property or function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "inject",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This keyword is used to inject a dependency into a class or a function. You can use it to automatically provide dependencies to your code without having to manually create and manage instances.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "viewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Declares a ViewModel, with lifecycle management integrated with Android’s Jetpack ViewModel. Works with Koin’s viewModel DSL.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Set Up Koin Context Correctly",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Initialize Koin using `KoinContext()` in the LocalComposition to avoid fallback issues and ensure that your app context is correctly propagated. KoinContext represents the global Koin instance. Once you start Koin with your module configuration, the context is available globally for dependency resolution. The Koin context is initialized when you call startKoin and can be stopped using stopKoin. You can retrieve dependencies from the context using methods like get() or inject().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Application DSL",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                SpanText(
                    text = "A KoinApplication instance is a Koin container instance configuration. This will let your configure logging, properties loading and modules.",
                    modifier = Modifier.padding(bottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "startKoin: Create and register KoinApplication instance. create a KoinApplication container configuration and register it in the GlobalContext to allow the use of GlobalContext API. That’s it! This is the basic Koin setup. As you can see the House class now even fetches its needed instance of Doorbell.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "modules: create a Koin Module or a submodule (inside a module). set a list of Koin modules to load in the container (list or vararg list)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                            val appModule = module {
                                includes(
                                    dispatcherModule
                                )
                            }

                            const val DISPATCHER_DEFAULT = "DISPATCHER_DEFAULT"
                            const val DISPATCHER_IO = "DISPATCHER_IO"
                            const val DISPATCHER_MAIN = "DISPATCHER_MAIN"

                            val dispatcherModule = module {
                                single<CoroutineDispatcher>(named(Const.DISPATCHER_DEFAULT)) { Dispatchers.Default }
                                single<CoroutineDispatcher>(named(Const.DISPATCHER_IO)) { Dispatchers.IO }
                                single<CoroutineDispatcher>(named(Const.DISPATCHER_MAIN)) { Dispatchers.Main }
                            }
                            """.trimIndent()
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "androidContext: Use the given Android context",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "koinComponent: For using koin features, tag the class with the same to get access to koin functions. Works well for singletons or globally available dependencies. Quick access, small apps, utilities.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "logger() describe what level and Logger implementation to use (by default use the EmptyLogger)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                    //1
                    install(Logging) {
                        logger = Logger.SIMPLE
                        level = LogLevel.BODY
                    }

                    //2
                    install(Logging) {
                        logger = object : Logger {
                            override fun log(message: String)
                            {
                                println(message)
                            }
                        }

                        level = LogLevel.ALL
                    }

                    //3
                    fun HttpClientConfig<*>.installLogs() {
                        install(Logging) {
                            logger = Logger.DEFAULT
                            level = LogLevel.ALL
                            logger = object : Logger {
                                override fun log(message: String) {
                                    log("KtorClient", message)
                                }
                            }
                        }
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Koin Scopes / Module DSL",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                SpanText(
                    text = "A Koin module gather definitions that you will inject/combine for your application. To create a new module, just use the following function:",
                    modifier = Modifier.margin(bottom = 122222.em)
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "module { // module content } - create a Koin Module",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "single: Creates an object that persistent with the entire container lifetime. You manually create the object inside a lambda. Works for any creation logic. The constructor needs values not managed by Koin.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "singleOf: singleOf is a Koin DSL function used to register a singleton by referencing a constructor or factory function directly. No custom creation logic is required. Uses constructor reference. Koin automatically resolves constructor parameters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        val secretsScannerDataKoinModule = 
                        module {
                            singleOf(::SecretsScannerRepositoryImplementation).bind(clazz = SecretsScannerRepository::class)
                            
                            includes(
                                module =
                                    listOf(secretsScannerLocalSourceKoinModule),
                            )
                        }
                    """.trimIndent()
                )

                //https://github.com/StringMeUp/KotlinMultiplatformSampleCMP
                KotlinCode(
                    code = $$"""
                        expect class KoinPlatform() {
                            val name: String
                        }

                        val platformModule = module {
                            singleOf(::KoinPlatform)
                        }

                        //iosMain
                        @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                        actual class KoinPlatform actual constructor() {
                            actual val name: String =
                                UIDevice.currentDevice.systemName() + " " + UIDevice.currentDevice.systemVersion
                        }

                        //androidMain
                        @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                        actual class KoinPlatform actual constructor() {
                            actual val name: String
                                get() = "Android ${Build.VERSION.SDK_INT}"
                        }
                        """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "factory: creates a new object each time. No persistence in the container. (Creates a new instance of a dependency each time it’s requested)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    const val BASE_URL = ""

                    val networkModule = module {
                        factory<HttpClient> {
                            HttpClient {
                                defaultRequest {
                                    url(BASE_URL)
                                }
                                install(ContentNegotiation) {
                                    json(
                                        Json {
                                            ignoreUnknownKeys = true
                                            prettyPrint = true
                                            isLenient = true
                                        }
                                    )
                                }
                                install(Logging){
                                    logger = Logger.SIMPLE
                                    level = LogLevel.BODY
                                }
                            }
                        }

                        factory<ArticlesNetworkDataSource> {
                            ArticlesNetworkDataSource(get())
                        }
                    }

                    /**
                     * Initializes the Koin modules.
                     *
                     * @param appModule the app module to be included
                     */
                    fun initKoin(appModule: Module = module { }) {
                        startKoin {
                            modules(appModules + appModule + platformModule)
                        }
                    }

                    internal val appModules = listOf(
                        networkModule
                    )

                    fun initKoin() = initKoin(
                        module {
                            includes(platformModule)
                        }
                    )

                    expect val platformModule: Module
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "scoped: creates an object that persists to associated scope lifetime",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "bind — additional Kotlin type binding for a given bean definition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "binds —  Types array for given bean definition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "getProperty() — resolve a property",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "scope() — define a logical group for scoped definition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "scoped { //definition }- provide a bean definition that will exists only in a scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "repositoryModule — contains examples of binding interfaces (domain’s repo interface with its actual implementation) you can read more here.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                SpanText(
                    text = "Inject Instances",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Once you’ve declared some modules and started Koin, you can retrieve your instances in your Android Activity Fragments or Services using.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Koin component extensions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "by inject() — lazy evaluated instance from Koin container. (lazy evaluated delegated instance)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "get() — eager fetch instance from Koin container. (eager access for instance)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                        private val signInUseCase by inject<SignInUseCase>()
                        private val signInUseCase = get<SignInUseCase>()
                    """.trimIndent()
            )

            SpanText(
                text = "What does scope means?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scope means you are providing restriction to your dependency, like you (dependency) cannot inject(use) outside from this scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suppose you have a class called `Component`and you want, the dependency of this class can only be injected(use) in MainActivity ,not outside from the MainActivity. So this type of things can be achieve through scopes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Koin we use Scope and Scoped function for providing scope to any dependency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Scope Definition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "single definition, create an object that persistent with the entire container lifetime (can't be dropped).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "factory definition, create a new object each time. Short live. No persistence in the container (can't be shared).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "scoped definition, create an object that persistent tied to the associated scope lifetime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Application Class - Auto Context Koin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Include all modules in modules() and create a KoinApplication container using startKoin{} function",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inject a context instance inside container using a androidContext() function & setup a Logger inside container using androidLogger() function",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "scoped definition, create an object that persistent tied to the associated scope lifetime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //2 https://github.com/frankliang610/NutriSport
            KotlinCode(
                code = """
                    //1 Koin
                    class MyApplication : Application(){

                        override fun onCreate() {
                            super.onCreate()
                            startKoin {
                                androidContext(this@MyApplication)
                                
                                androidLogger()
                                modules(networkModule, remoteDataSourceModule, repositoryModule, viewModelModule, dataBaseModule)
                            }
                        }
                    }

                    <application android:name=".MyApplication" />
                    
                    //2
                    class MyApplication: Application() {
                        override fun onCreate() {
                            super.onCreate()
                            initializekoin(
                                config = {
                                    androidContext(this@MyApplication)
                                }
                            )
                            Firebase.initialize(context = this)
                        }
                    }
                    
                    fun initializeKoin(
                        config: (KoinApplication.() -> Unit)? = null
                    ) {
                        startKoin {
                            config?.invoke(this)
                        }
                    }
                    
                    //3
                    class TechnicalTestApplication : Application() {
                        override fun onCreate() {
                            super.onCreate()

                            Koin().init {
                                androidLogger(level = if (BuildConfig.DEBUG) Level.ERROR else Level.NONE)
                                androidContext(androidContext = this@TechnicalTestApplication)
                            }
                        }
                    }
                    
                    //4 Manually
                    import android.app.Application

                    class WanApplication : Application() {

                        companion object {
                            lateinit var instance: WanApplication
                                private set
                        }

                        override fun onCreate() {
                            super.onCreate()
                            instance = this
                        }
                    }
                    //Access - WanApplication.instance
                    
                    //5 Manually
                    import android.annotation.SuppressLint
                    import android.content.Context

                    @SuppressLint("StaticFieldLeak")
                    object AndroidAppContext {
                        private var context: Context? = null

                        fun init(context: Context) {
                            AndroidAppContext.context = context.applicationContext
                        }

                        fun getApplicationContext(): Context? {
                            return context
                        }
                    }
                    
                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)

                            AndroidAppContext.init(this)
                            enableEdgeToEdge()
                        }
                    }
                    // <activity android:name=".MainActivity" />
                    """.trimIndent()
            )

            SpanText(
                text = "GlobalContext",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/music-assistant/mobile-app.git
            KotlinCode(
                code = """
                    singleOf(::ServiceClient)
                    single(createdAtStart = true) { AuthenticationManager(get(), get()) } 
                    singleOf(::MainDataSource)          // Singleton - held by foreground service
                    
                    /**
                     * Cleanup function to properly close all singleton resources.
                     * Call this before stopKoin() to ensure proper resource cleanup.
                     */
                    fun cleanupSingletons() {
                        try {
                            GlobalContext.getOrNull()?.get<ServiceClient>()?.close()
                            GlobalContext.getOrNull()?.get<AuthenticationManager>()?.close()
                            GlobalContext.getOrNull()?.get<MainDataSource>()?.close()
                        } catch (e: Exception) {
                            // Ignore exceptions during cleanup
                        }
                    }
                    
                    class MyApplication : Application() {
                        override fun onTerminate() 
                        {
                            super.onTerminate()
                            // Clean up resources when app is terminated
                            // Note: This is called on emulator/testing but not on real devices
                            // However, having it ensures proper cleanup in tests
                            cleanupSingletons()
                            stopKoin()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //WebApp use GlobalContext
                    
                    @Composable
                    fun AuthScreen(
                        onNavigateBack: () -> Unit,
                        onLoginSuccess: () -> Unit,
                        onLogoutSuccess: () -> Unit = {}
                    ) {
                        val viewModel = remember { GlobalContext.get().get<AuthViewModel>() }
                    }
                    
                    class AuthViewModel(
                        private val getAuthState: GetAuthStateUseCase
                    ) {
                    
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Multiple module add",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class GetMoviesUseCase : KoinComponent {
                        private val repository: MovieRepository by inject()
                    }

                    interface MovieRepository { }

                    class HomeViewModel(
                        val getMoviesUseCase: GetMoviesUseCase
                    ): ViewModel(){ }

                    //Access only compose function
                    val homeViewModel: HomeViewModel = koinViewModel()

                    //Multiple module add
                    startKoin {
                        modules(appModule + getSharedModules())
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    Module add separate view
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun initKoin(
                        useCasesModule: Module = Modules.useCases,
                        viewModelsModule: Module = module { },
                    ): KoinApplication = startKoin {
                        modules(
                            useCasesModule,
                            viewModelsModule
                        )
                    }

                    private val viewModelModule = module {
                        viewModel {  }
                    }

                    initKoin(appModule = appModule, viewModelsModule = viewModelModule)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class ExpenseTrackerApp : Application() {
                        initKoin(viewModelsModule = module {
                          viewModel {

                          }
                          viewModel {

                          }
                        }, appModule = module {
                          single<Context> { this@ExpenseTrackerApp }
                        })
                    }

                    fun initKoin(
                      appModule: Module = module { },
                    ): KoinApplication = startKoin { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun initKoin(appDeclaration: KoinAppDeclaration = {}) = startKoin {
                        // This gives Koin modules access to the Context from Android
                        appDeclaration()
                        modules(
                            firestoreModule,
                            repositoryModule,
                            useCaseModule
                        )
                    }

                    // IOS
                    fun initKoin() = initKoin {}

                    val firestoreModule = module {
                        single { Firebase.firestore }
                    }

                    val repositoryModule = module {
                        singleOf(::FirestoreRepositoryImpl) bind FirestoreRepository::class
                    }

                    val useCaseModule = module {
                        factoryOf(::GetSneakersUseCase)
                        factoryOf(::GetSneakerByIdUseCase)
                    }
                    class GetSneakersUseCase(
                        private val firestoreRepository: FirestoreRepository
                    ){
                        operator fun invoke(search:String):Flow<List<SneakerDTO>> = flow {
                            emit( firestoreRepository.getSneakers(search) )
                        }
                    }

                    class GetSneakerByIdUseCase(
                        private val firestoreRepository: FirestoreRepository
                    ) {
                        operator fun invoke(id:String):Flow<SneakerDTO> = flow {
                            emit( firestoreRepository.getSneakerById(id) )
                        }
                    }
                    class SneakerInfoViewModel(
                        private val getSneakerByIdUseCase: GetSneakerByIdUseCase
                    ) : ViewModel() {

                        private val _responseSneaker:MutableStateFlow<SneakerDTO?> = MutableStateFlow(null)
                        val responseSneaker = _responseSneaker.asStateFlow().filterNotNull()

                        fun getSneakerById(id:String){
                            getSneakerByIdUseCase.invoke(id).onEach {
                                _responseSneaker.value = it
                            }.launchIn(viewModelScope)
                        }
                    }

                    class SneakersViewModel(
                        private val getSneakersUseCase: GetSneakersUseCase,
                        getAdvertisingUseCase: GetAdvertisingUseCase
                    ) : ViewModel() {

                        private val _responseSneakers = MutableStateFlow(emptyList<SneakerDTO>())
                        val responseSneakers = _responseSneakers.asStateFlow()

                        val responseAdvertising = getAdvertisingUseCase.invoke()
                            .stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())

                        fun getSneakers(search:String){
                            getSneakersUseCase.invoke(search).onEach {
                                _responseSneakers.value = it
                            }.launchIn(viewModelScope)
                        }
                    }

                    @Composable
                    fun HomeScreen(
                        viewModel:SneakersViewModel = getViewModel(),
                        navController: NavController
                    ) {
                        var advertising by rememberSaveable { mutableStateOf(emptyList<Advertising>()) }

                        viewModel.responseAdvertising.onEach {
                            advertising = it
                        }.launchWhenStarted()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Koin DI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val dataSourceModule = module {
                        //Gson
                        single<Gson> { Gson() }

                        //context
                        single{ ReminderManger(context = androidContext()) }

                        //firebase
                        single<FirebaseMessaging> { FirebaseMessaging.getInstance() }
                        single<FirebaseAuth> { FirebaseAuth.getInstance() }
                        single<FirebaseFirestore>{ Firebase.firestore}

                        //interface
                        single<DataStoreDataSource> { DataStoreDataSourceImp(androidContext()) }
                    }

                    class OApplication : Application() {
                        override fun onCreate() {
                            super.onCreate()
                            startKoin {
                                androidLogger()
                                androidContext(this@OApplication)
                                modules(
                                    dataSourceModule
                                )
                            }
                        }
                    }

                    //manifest
                    <application
                            android:name=".OApplication">
                    </application>
                    """.trimIndent()
            )

            SpanText(
                text = "viewModelOf",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val viewModelModule = module {
                        viewModelOf(::SneakersViewModel)
                        viewModelOf(::SneakerInfoViewModel)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "object DI withOptions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    object ExpenseManager {
                     val fakeExpenseList = mutableListOf(
                            Expense(
                                id = currentId++,
                                amount = 70.0,
                                category = ExpenseCategory.GROCERIES,
                                description = "Weekly buy"
                            )
                        )
                    }

                    //di
                    single { ExpenseManager }.withOptions { createdAtStart() }

                    interface ExpenseRepository {
                        fun getAllExpenses(): List<Expense>
                    }

                    class ExpenseRepoImpl(private val expenseManager: ExpenseManager) : ExpenseRepository {
                        override fun getAllExpenses(): List<Expense> {
                            return expenseManager.fakeExpenseList
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val sharedModule = module {

                        single<ExampleRepository> {
                            ExampleRepositoryImpl()
                        }.withOptions {
                            createdAtStart()
                            named("ExampleRepository")
                        }

                        single {
                            HomeViewModel(exampleRepository = get(qualifier = qualifier("ExampleRepository")))
                        }
                    }

                    class HomeViewModel(private val exampleRepository: ExampleRepository) : ViewModel() { }

                    interface ExampleRepository {
                        suspend fun getExample(): List<String>
                    }

                    class ExampleRepositoryImpl : ExampleRepository {
                        override suspend fun getExample(): List<String> {
                            return listOf("Flutter", "Jetpack compose", "KMP", "Kobweb")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "named koin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                val appModule =
                    module {
                        single(named("apiKey")) { BuildConfig.API_KEY }
                        single(named("baseHost")) { BuildConfig.BASE_HOST }
                    }

                //USE
                get(named("apiKey")
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    val httpClientModule = module {
                            single(named("movies")) {
                            HttpClient {
                                /*defaultRequest {
                                    url(FileDownloadConstants.BASE_URL)
                                }*/
                                install(Auth) {
                                    bearer {
                                        loadTokens {
                                            BearerTokens(
                                                accessToken = "",
                                                refreshToken =""
                                            )
                                        }
                                    }
                                }
                                install(HttpTimeout) {
                                    val timeout = 30000L
                                    connectTimeoutMillis = timeout
                                    requestTimeoutMillis = timeout
                                    socketTimeoutMillis = timeout
                                }
                                install(ContentNegotiation) {
                                    json(
                                        Json {
                                            ignoreUnknownKeys = true
                                            prettyPrint = true
                                            isLenient = true
                                        }
                                    )
                                }
                                install(Logging) {
                                    level = LogLevel.ALL
                                    logger = object : Logger {
                                        override fun log(message: String) {
                                            Napier.i(tag = "Http Client", message = message)
                                        }
                                    }
                                }.also {
                                    Napier.base(DebugAntilog())
                                }
                            }
                        }

                        single(named("fileDownload")) {
                            HttpClient {

                            }
                        }
                    }

                    fun initKoin(declaration: KoinAppDeclaration = {}) =
                        startKoin {
                            declaration()

                            modules(
                                httpClientModule
                            )
                        }

                    fun initKoin() = initKoin {}

                    val networkServiceModule = module {
                        single<MoviesSource> {
                            MoviesSourceImpl(httpClient = get(named("movies")))
                        }
                        single<FileDownloadSource> {
                            FileDownloadSourceImpl(httpClient = get(named("fileDownload")))
                        }
                    }

                    class MoviesSourceImpl(
                        private val httpClient: HttpClient,
                        private val baseUrl: String = ApiConstants.BASE_URL
                    ) : MoviesSource {

                    }

                    interface MoviesSource {
                    }
                    """.trimIndent()
            )

            //https://github.com/sajibsaj66/Ndulaz
            //https://github.com/xephosbot/AnilibriaRefresh
            KotlinCode(
                code = """
                    //get
                    import androidx.compose.runtime.Composable
                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.viewmodel.compose.viewModel
                    import org.koin.compose.currentKoinScope

                    @Composable
                    inline fun <reified T : ViewModel> koinViewModel(): T {
                        val scope = currentKoinScope()
                        return viewModel {
                            scope.get<T>()
                        }
                    }

                    //Use
                    1. val appViewModel: AppViewModel = koinViewModel()
                    2. val settingsViewModel = getKoinViewModel<SettingsViewModel>()
                    
                    //getAll
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import org.koin.compose.currentKoinScope
                    import org.koin.core.scope.Scope

                    @Composable
                    inline fun <reified T> koinInjectAll(
                        scope: Scope = currentKoinScope()
                    ): List<T> {
                        return remember(scope) {
                            scope.getAll(T::class)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun startApp(nativeAppDeclaration: KoinAppDeclaration? = null) {
                        startKoin {
                            nativeAppDeclaration?.let { it() }
                        }
                    }

                    //ios
                    struct ContentView: View {
                    	let greet = Greeting().greet()

                    	var body: some View {
                    		Text(greet)
                    	}
                    }

                    //android
                    class MainActivity : ComponentActivity(), KoinComponent {
                        private val greeting: Greeting = get()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "KoinDefinition Using icerock.moko",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import dev.icerock.moko.mvvm.viewmodel.ViewModel
                    import org.koin.core.definition.Definition
                    import org.koin.core.definition.KoinDefinition
                    import org.koin.core.module.Module
                    import org.koin.core.qualifier.Qualifier

                    /**
                     * Defines a multiplatform [ViewModel] in the Koin module.
                     */
                    expect inline fun <reified T : ViewModel> Module.viewModelDefinition(
                        qualifier: Qualifier? = null,
                        noinline definition: Definition<T>,
                    ): KoinDefinition<T>

                    //viewModelDefinition
                    val settingsModule = module {
                        viewModelDefinition { SettingSourcesScreenViewModel(get()) }
                    }

                    //iosMain
                    import dev.icerock.moko.mvvm.viewmodel.ViewModel
                    import org.koin.core.definition.Definition
                    import org.koin.core.definition.KoinDefinition
                    import org.koin.core.module.Module
                    import org.koin.core.qualifier.Qualifier

                    /**
                     * Defines an iOS [ViewModel] in the Koin module.
                     */
                    actual inline fun <reified T : ViewModel> Module.viewModelDefinition(
                        qualifier: Qualifier?,
                        noinline definition: Definition<T>,
                    ): KoinDefinition<T> = factory(qualifier = qualifier, definition = definition)

                    //androidMain
                    import dev.icerock.moko.mvvm.viewmodel.ViewModel
                    import org.koin.androidx.viewmodel.dsl.viewModel
                    import org.koin.core.definition.Definition
                    import org.koin.core.definition.KoinDefinition
                    import org.koin.core.module.Module
                    import org.koin.core.qualifier.Qualifier

                    /**
                     * Defines an Android [ViewModel] in the Koin module.
                     */
                    actual inline fun <reified T : ViewModel> Module.viewModelDefinition(
                        qualifier: Qualifier?,
                        noinline definition: Definition<T>,
                    ): KoinDefinition<T> = viewModel(qualifier = qualifier, definition = definition)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //commonMain
                    expect class MyBatteryManager {
                        fun getBatteryLevel(): Int
                    }

                    //iosMain
                    actual class MyBatteryManager {
                        actual fun getBatteryLevel(): Int {
                            UIDevice.currentDevice.batteryMonitoringEnabled = true
                            val batteryLevel = UIDevice.currentDevice.batteryLevel

                            return (batteryLevel * 100).roundToInt()
                        }
                    }

                    fun MainViewController() = ComposeUIViewController {
                        App(
                            batteryManager = remember { MyBatteryManager() }
                        )
                    }

                    //androidMain
                    actual class MyBatteryManager(
                        private val context: Context
                    ) {
                        actual fun getBatteryLevel(): Int {
                            val intentFilter = IntentFilter(Intent.ACTION_BATTERY_CHANGED)
                            val batteryStatus = context.registerReceiver(null, intentFilter)
                            val level = batteryStatus?.getIntExtra(BatteryManager.EXTRA_LEVEL, -1) ?: -1
                            val scale = batteryStatus?.getIntExtra(BatteryManager.EXTRA_SCALE, -1) ?: -1
                            return (level * 100 / scale.toFloat()).roundToInt()
                        }
                    }

                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)

                            setContent {
                                App(
                                    batteryManager = remember {
                                        MyBatteryManager(applicationContext)
                                    }
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    inline fun <reified T: ViewModel> NavBackStackEntry.sharedViewModel(navController: NavController): T {
                        val navGraphRoute = this.destination.parent?.route ?: return koinViewModel<T>()

                        val parentEntry = remember(this) {
                            navController.getBackStackEntry(navGraphRoute)
                        }

                        return koinViewModel(viewModelStoreOwner = parentEntry)
                    }
                    """.trimIndent()
            )
        }
    }
}

//single { DatabaseDriverFactory() }
//single { SharedDatabase(get()) }
//single<GeminiService> { GeminiServiceImpl(get()) }
//factory { GetAllGoalsUseCase(get()) }
//viewModelOf(::GamificationViewModel)

/*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import org.koin.compose.currentKoinScope
import org.koin.core.scope.Scope

@Composable
inline fun <reified T> koinInjectAll(
    scope: Scope = currentKoinScope()
): List<T> {
    return remember(scope) {
        scope.getAll(T::class)
    }
}

@Composable
internal fun AnilibriaNavGraph(
    modifier: Modifier = Modifier,
    navigator: Navigator<NavBackStackEntry>,
    startNavKey: NavKey = HomeRoute,
    navEntryBuilders: List<NavEntryBuilder> = koinInjectAll()
) { }

typealias NavEntryBuilder = NavGraphBuilder.(Navigator<*>) -> Unit

single<NavEntryBuilder>(named("feature/home")) {
        { navigator ->
            composable<HomeRoute> {
                HomeScreen(
                    onSearchClick = {
                        navigator.navigateToSearch()
                    },
                    onReleaseClick = { releaseId ->
                        navigator.navigateToTitle(releaseId)
                    }
                )
            }
        }
    }
*/

//@Provided
//https://medium.com/@beranger.guillaume/working-with-koin-annotations-the-ultimate-di-setup-bc81d6c89440
//https://www.droidcon.com/2025/11/13/bridging-two-worlds-using-kmp-dependencies-with-koin-in-android-native-with-hilt/
//private val fnOfficialApi: FnOfficialApiImpl by inject(FnOfficialApiImpl::class.java)
//Use viewModel {} → survives rotation, scoped to Activity/Fragment/NavBackStackEntry.
//Use scope { viewModel { } } → survives only while the scope is alive (you manually close it).
//For Compose Navigation, koinViewModel() automatically binds to the backstack entry.

//https://github.com/donald-okara/ScopeExampl.git
//https://github.com/orioneee/Axer (Table View, IsolatedContext koin)
//https://github.com/Beb3r/masterdetailshowcase.git (Module scan)
//https://github.com/mccostic/NBRideMultiPlatform (Mapper DI)
//https://github.com/VolodymyrPal/TrainingSession.git (List Mapper, Koin Annotations)
//https://github.com/vincenzosarnataro/UsersKMP.git (Custom Header, Retrofit, Koin Annotations)
//https://github.com/Ma7moud3ly/quran-multiplatform.git (Koin Annotations, KMM Plugin Annotations, Font Access Annotations)
//https://github.com/vincenzosarnataro/UsersKMP.git (Koin Annotations, KTOR with K-RETROFIT, KTOR custom header)
//https://github.com/niltsiar/kotlin_multiplatform_pokedex.git (koin, koin named, pagination)
//https://github.com/AnatoliiChub/HRAM (Koin Annotation  @WorkerThread, @WorkerIOThread, @Qualifier, @Module, @Module(includes = []), @Configuration, @Single, @Single(binds = []),  @Factory, @KoinViewModel, )
//https://github.com/romreviewer/NoteItUP.git (koin context)
//https://github.com/tomil740/MilkFlowNative.git (Koin Worker, CoroutineWorker)
//https://medium.com/@spparks_/koin-scaling-your-project-with-annotations-ksp-and-navigation-3-integration-273abe767a4b
//https://github.com/vincenzosarnataro/UsersKMP.git (annotations)
//https://github.com/baselinesio/baselines-kmp-free (Metro di)
//https://github.com/raheemadamboev/cmp-koin-annotations
//https://github.com/Ma7moud3ly/quran-multiplatform.git (koin annotation using font)
//https://github.com/tezov/koin-education.git
//https://github.com/Grippo-app/grippo-mobile.git (Dispatchers.IO - runCatching httpClient, Koin Annotations)
//https://fakhrulasa.hashnode.dev/koin

/*
Define a hashtag#Koin module in shared code (commonMain) for easy injection across Android and Compose (by inject(), koinInject()). In iOS, use the KoinPlatform API to export a pure Kotlin function for injection.

 factory<InsertUserUseCase> {
        InsertUserUseCaseImp(get<SpendLessDataSource>())
    }
*/
//https://blog.insert-koin.io/koin-framework-2025-roadmap-from-4-0-to-future-milestones-68b0558e56a9
//https://blog.insert-koin.io/koin-annotations-2-2-jsr-330-compatibility-smart-configurations-and-scope-archetypes-5b2f4c8536c1
//https://medium.com/@beranger.guillaume/working-with-koin-annotations-the-ultimate-di-setup-bc81d6c89440
//https://blog.insert-koin.io/koin-powered-by-kotlin-compiler-0722f1cc96a4
//https://itnext.io/dependency-injection-with-koin-linked-scopes-a305c05f36a7

//https://itnext.io/dependency-injection-with-koin-the-minimal-setup-kmp-d696ae2db09f

//https://medium.com/@tezov.app
//https://medium.com/@actiwerks/koin-and-context-management-in-android-simplifying-or-adding-complexity-568b58bf2296
//https://blog.insert-koin.io/detekt-rules-koin-c0b6330fc37b